<template>
  <div class="bg-page font-body text-ink flex h-full flex-col">
    <!-- Top bar -->
    <header v-if="!isWebView" class="border-hairline sticky top-0 z-30 flex h-15 shrink-0 items-center gap-3.5 border-b px-4 backdrop-blur-xl md:px-6" style="background: var(--portal-chrome)">
      <RouterLink :to="{ name: 'home' }" class="text-ink flex items-center" aria-label="Invoxia">
        <InvoxiaLogo :width="92" :height="23" color="currentColor" />
      </RouterLink>

      <span class="flex-1"></span>

      <UserMenu />
    </header>

    <div class="flex min-h-0 flex-1">
      <!-- Sidebar (desktop) -->
      <aside v-if="!isWebView" class="border-hairline bg-surface sticky top-0 hidden h-full w-62 shrink-0 flex-col gap-1 border-r px-4 py-5.5 md:flex">
        <SectionLabel tone="ink" class="mx-3 mt-2 mb-1.5">{{ t('shell.customerArea') }}</SectionLabel>
        <component
          :is="isDisabled(item) ? 'div' : RouterLink"
          v-for="item in navItems"
          :key="item.routeName"
          :to="isDisabled(item) ? undefined : { name: item.routeName }"
          class="font-display flex items-center gap-3 rounded-xl px-3.5 py-2.75 text-[14.5px] font-medium no-underline transition-colors"
          :class="
            isDisabled(item)
              ? 'text-ink cursor-not-allowed opacity-50'
              : isActive(item)
                ? 'bg-primary-100 text-primary-600 dark:bg-primary-500/20 dark:text-white'
                : 'text-ink hover:bg-grey-100 dark:hover:bg-white/5'
          "
        >
          <span aria-hidden="true" class="h-5 w-5 shrink-0 bg-current" :class="isActive(item) ? 'opacity-100' : 'opacity-60'" :style="iconMask(item.icon)"></span>
          {{ item.label }}
          <Skeleton v-if="item.routeName === 'subscription' && devicesLoading" rounded="full" class="ml-auto h-4.25 w-6" />
          <CountChip v-else-if="item.count" :count="item.count" :active="isActive(item)" class="ml-auto" />
        </component>
      </aside>

      <!-- Routed content + footer -->
      <main ref="mainEl" class="flex min-w-0 flex-1 flex-col overflow-auto px-4 pt-4.5 pb-6 [scrollbar-gutter:stable] md:px-10 md:pt-8.5 md:pb-16">
        <div class="flex-1">
          <slot />
        </div>
        <PortalFooter v-if="!isWebView" />
      </main>
    </div>

    <!-- Bottom tab bar (mobile) -->
    <nav
      v-if="!isWebView"
      class="border-hairline sticky bottom-0 z-30 flex shrink-0 justify-around border-t px-1.5 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] backdrop-blur-lg md:hidden"
      style="background: var(--portal-chrome)"
    >
      <component
        :is="isDisabled(item) ? 'div' : RouterLink"
        v-for="item in navItems"
        :key="item.routeName"
        :to="isDisabled(item) ? undefined : { name: item.routeName }"
        class="font-display flex flex-1 flex-col items-center gap-0.75 px-0.5 py-1 text-[10px] font-semibold no-underline"
        :class="isDisabled(item) ? 'text-faint cursor-not-allowed opacity-50' : isActive(item) ? 'text-primary-600 dark:text-primary-300' : 'text-faint'"
      >
        <span aria-hidden="true" class="h-5.5 w-5.5 bg-current" :class="isActive(item) ? 'opacity-100' : 'opacity-60'" :style="iconMask(item.icon)"></span>
        {{ item.label }}
      </component>
    </nav>

    <ToastContainer />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'
import { useStore } from 'vuex'

import PortalFooter from '@/components/layout/PortalFooter.vue'
import UserMenu from '@/components/layout/UserMenu.vue'
import CountChip from '@/components/ui/CountChip.vue'
import SectionLabel from '@/components/ui/SectionLabel.vue'
import Skeleton from '@/components/ui/Skeleton.vue'
import ToastContainer from '@/components/ui/ToastContainer.vue'
import { homeMessages } from '@/lang/pages/home'
import InvoxiaLogo from '@/svg/invoxiaLogo.vue'
import type { Device } from '@/types/devices.api'

const { t } = useI18n({ useScope: 'local', messages: homeMessages, inheritLocale: true })
const route = useRoute()
const store = useStore()

// In the mobile-app webview the native app provides its own chrome — suppress
// the portal top bar, sidebar, mobile tab bar and Log out control (matches the
// old LayoutNavBar `v-if="!isWebView"` behaviour).
// The window never scrolls (#app is h-dvh overflow-hidden) — <main> is the
// scroll container, so router scrollBehavior can't reset it between pages.
const mainEl = ref<HTMLElement | null>(null)
watch(
  () => route.path,
  () => mainEl.value?.scrollTo({ top: 0 }),
)

const isWebView = computed<boolean>(() => store.getters['users/isWebView'])
const devices = computed<Device[]>(() => store.getters['devices/getDevices'] || [])
const devicesLoading = computed<boolean>(() => store.getters['devices/getDevicesLoading'])

const navItems = computed(() => [
  { routeName: 'home', icon: 'home', label: t('nav.home'), count: 0 },
  { routeName: 'subscription', icon: 'tag', label: t('nav.subscriptions'), count: devices.value.length },
  { routeName: 'orders', icon: 'package', label: t('nav.orders'), count: 0 },
  { routeName: 'subscriptionInvoices', icon: 'info', label: t('nav.invoices'), count: 0 },
  { routeName: 'partners', icon: 'handshake', label: t('nav.partners'), count: 0 },
])

type NavItem = (typeof navItems.value)[number]

const iconSrc = (name: string) => `/images/portal-icons/${name}.svg`

// The nav icons are solid-black SVGs; masking them onto bg-current keeps them in
// the item's text color in both schemes (active blue, inactive grey), where an
// <img> would need a dark-mode invert that fights the active state.
const iconMask = (name: string) => {
  const mask = `url('${iconSrc(name)}') center / contain no-repeat`
  return { mask, '-webkit-mask': mask }
}
const isActive = (item: NavItem) => route.name === item.routeName || (item.routeName === 'subscription' && route.matched.some((r) => r.name === 'subscription'))

// Grey out guarded destinations (everything but Home) once the backend has said
// the email is unconfirmed — they're behind requireEmailVerification and would
// silently bounce. `null` only means fetchUserRole hasn't resolved yet, so it
// must not flash the disabled state on a verified account.
const emailConfirmed = computed(() => store.getters['users/getEmailConfirmed'])
const isDisabled = (item: NavItem) => item.routeName !== 'home' && emailConfirmed.value === false
</script>
